import { useState, useEffect, useMemo } from 'react';
import { UnifiedCourseService } from '@/services/unifiedCourseService';
import { Course } from '@/hooks/useCourses';
import { Module } from '@/types/courseTypes';
import { toast } from 'sonner';
import { logger } from '@/utils/logger';

interface UseEnhancedCoursesOptions {
  category?: string;
  search?: string;
  filters?: {
    level?: string;
    duration?: 'short' | 'medium' | 'long';
  };
  enableCaching?: boolean;
}

export const useEnhancedCourses = (options: UseEnhancedCoursesOptions = {}) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    category,
    search,
    filters,
    enableCaching = true
  } = options;

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);

      let data: Course[];

      if (search) {
        data = await UnifiedCourseService.searchCourses(search, {
          category,
          ...filters
        });
      } else if (category) {
        data = await UnifiedCourseService.getCoursesByCategory(category);
      } else {
        data = await UnifiedCourseService.getAllCourses();
      }

      setCourses(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch courses';
      logger.error('Error fetching enhanced courses:', err);
      setError(errorMessage);
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, [category, search, JSON.stringify(filters), enableCaching]);

  const stats = useMemo(() => {
    return {
      totalCourses: courses.length,
      averageDuration: courses.reduce((sum, c) => sum + (c.duration_hours || 0), 0) / courses.length || 0,
      levels: courses.reduce((acc, c) => {
        acc[c.level] = (acc[c.level] || 0) + 1;
        return acc;
      }, {} as Record<string, number>),
      categories: courses.reduce((acc, c) => {
        acc[c.category] = (acc[c.category] || 0) + 1;
        return acc;
      }, {} as Record<string, number>)
    };
  }, [courses]);

  return {
    courses,
    loading,
    error,
    stats,
    refetch: fetchCourses
  };
};

export const useEnhancedCourse = (courseId: string) => {
  const [course, setCourse] = useState<(Course & { modules: Module[] }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCourse = async () => {
    try {
      setLoading(true);
      setError(null);

      const courseData = await UnifiedCourseService.getCourseById(courseId);
      const modulesData = await UnifiedCourseService.getCourseModules(courseId);
      
      const data = courseData ? { ...courseData, modules: modulesData } : null;
      setCourse(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch course';
      logger.error('Error fetching enhanced course:', err);
      setError(errorMessage);
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (courseId) {
      fetchCourse();
    }
  }, [courseId]);

  return {
    course,
    loading,
    error,
    refetch: fetchCourse
  };
};

export const useEnhancedModule = (moduleId: string, courseId: string) => {
  const [module, setModule] = useState<Module | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchModule = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await UnifiedCourseService.getCompleteModule(moduleId, courseId);
      setModule(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch module';
      logger.error('Error fetching enhanced module:', err);
      setError(errorMessage);
      toast.error('Failed to load module');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (moduleId && courseId) {
      fetchModule();
    }
  }, [moduleId, courseId]);

  return {
    module,
    loading,
    error,
    refetch: fetchModule
  };
};

export const useCourseAnalytics = (courseId?: string) => {
  const [analytics, setAnalytics] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      // For now, return mock analytics since UnifiedCourseService doesn't have this method
      const data = {
        totalCourses: 0,
        totalEnrollments: 0,
        completionRate: 0,
        popularTopics: [],
        recentActivity: []
      };
      setAnalytics(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch analytics';
      logger.error('Error fetching course analytics:', err);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [courseId]);

  return {
    analytics,
    loading,
    error,
    refetch: fetchAnalytics
  };
};